import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../services/api';
import { DAYS_WINDOW_DEFAULT } from '../constants/periods';
import { AddDays, ParseIsoDate, TodayUtc } from '../utils/dateUtils';

const StoreContext = createContext(null);

/**
 * Converte snapshot do backend pra estrutura de Store usável:
 *   - dailyByDate vira Map (mais rápido pra range queries)
 *   - mlbsBySku acessível direto
 *   - tagsBySku como Map
 */
function HydrateSnapshot(raw) {
    if (!raw) return null;
    const salesBySku = new Map();
    Object.entries(raw.salesBySku || {}).forEach(([sku, entry]) => {
        const dailyByDate = new Map();
        Object.entries(entry.dailyByDate || {}).forEach(([iso, qty]) => {
            dailyByDate.set(iso, Number(qty || 0));
        });
        // orderTotalsByDate: Map<isoDate, {paid, cancelled, other}> com
        // contagem de pedidos DISTINTOS por status por dia. Selector clipa
        // ao período e soma — evita leak de pedidos fora da janela ativa.
        const orderTotalsByDate = new Map();
        Object.entries(entry.orderTotalsByDate || {}).forEach(([iso, bucket]) => {
            orderTotalsByDate.set(iso, {
                paid: Number(bucket?.paid || 0),
                cancelled: Number(bucket?.cancelled || 0),
                other: Number(bucket?.other || 0),
            });
        });
        salesBySku.set(sku, {
            dailyByDate,
            orderTotalsByDate,
            mlbs: Array.isArray(entry.mlbs) ? entry.mlbs : [],
            titleSample: entry.titleSample || null,
        });
    });

    const salesByMlb = new Map();
    Object.entries(raw.salesByMlb || {}).forEach(([mlb, entry]) => {
        const dailyByDate = new Map();
        Object.entries(entry.dailyByDate || {}).forEach(([iso, qty]) => {
            dailyByDate.set(iso, Number(qty || 0));
        });
        // Espelho do salesBySku: pedidos distintos por status por dia. Selector
        // clipa ao período ativo e soma → coluna orderCount no modo MLB.
        const orderTotalsByDate = new Map();
        Object.entries(entry.orderTotalsByDate || {}).forEach(([iso, bucket]) => {
            orderTotalsByDate.set(iso, {
                paid: Number(bucket?.paid || 0),
                cancelled: Number(bucket?.cancelled || 0),
                other: Number(bucket?.other || 0),
            });
        });
        salesByMlb.set(mlb, {
            sku: entry.sku || null,
            title: entry.title || null,
            dailyByDate,
            orderTotalsByDate,
        });
    });

    // stockBySku é plain object (acesso direto via stockBySku[sku]).
    // Backend já normaliza chaves em UPPERCASE.
    const stockBySku = raw.stockBySku || {};
    const tagsBySku = new Map(Object.entries(raw.tagsBySku || {}));
    // Tags por anúncio (inventory_mlb_tags). Cada entry = { tags: [], sku }.
    const tagsByMlb = new Map();
    Object.entries(raw.tagsByMlb || {}).forEach(([mlb, payload]) => {
        tagsByMlb.set(mlb, {
            tags: Array.isArray(payload?.tags) ? payload.tags : [],
            sku: payload?.sku || null,
        });
    });
    // Metadata dos anúncios (ml_listings). Title atualizado pelo sync de orders.
    const mlListingsByMlb = new Map();
    Object.entries(raw.mlListingsByMlb || {}).forEach(([mlb, entry]) => {
        mlListingsByMlb.set(mlb, {
            sku: entry?.sku || null,
            title: entry?.title || null,
            permalink: entry?.permalink || null,
            // inventory_id do Full (null em anúncio sem Full).
            inventoryId: entry?.inventory_id || null,
            thumbnail: entry?.thumbnail || null,
        });
    });

    // Anchor date: "hoje" UTC baseado no asOf do servidor. Mesmo fuso dos
    // buckets `dailyByDate` do backend (que usam `getUTCDate()`).
    const anchorDateIso = TodayUtc(raw.asOf);

    return {
        asOf: raw.asOf,
        anchorDateIso,
        range: raw.range,
        products: Array.isArray(raw.products) ? raw.products : [],
        stockBySku,
        salesBySku,
        salesByMlb,
        tagsBySku,
        tagsByMlb,
        mlListingsByMlb,
        ordersCount: Number(raw.ordersCount || 0),
        truncated: Boolean(raw.truncated),
    };
}

export const SNAPSHOT_TRUNCATED_HINT =
    'Resultado truncado pelo backend (500k orders). Filtre um período menor pra cobertura completa.';

/**
 * useOperacoesLoader: hook interno do Provider. Fetcha o snapshot do backend.
 * Refetch dispara quando o range pedido aumenta (preset > 90d) ou usuário
 * clica em Atualizar.
 */
function useLoader(initialRangeDays = DAYS_WINDOW_DEFAULT) {
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadedRangeDays, setLoadedRangeDays] = useState(0);
    const inFlight = useRef(false);

    const fetchSnapshot = useCallback(async (rangeDays) => {
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        setError(null);
        try {
            // Range é UTC puro. Backend bucketa por `getUTCDate()` — frontend
            // pede dia inteiro UTC. Sem conversão BR/UTC em lugar nenhum.
            const todayIso = TodayUtc();
            const todayDate = ParseIsoDate(todayIso);
            const fromDate = AddDays(todayDate, -(rangeDays - 1));
            const fromUtc = new Date(
                Date.UTC(fromDate.getUTCFullYear(), fromDate.getUTCMonth(), fromDate.getUTCDate(), 0, 0, 0, 0),
            );
            const toUtc = new Date(
                Date.UTC(todayDate.getUTCFullYear(), todayDate.getUTCMonth(), todayDate.getUTCDate(), 23, 59, 59, 999),
            );
            const params = new URLSearchParams({
                from: fromUtc.toISOString(),
                to: toUtc.toISOString(),
            });
            const raw = await api.get(`/operacoes/snapshot?${params.toString()}`);
            const hydrated = HydrateSnapshot(raw);
            setSnapshot(hydrated);
            setLoadedRangeDays(rangeDays);
        } catch (err) {
            setError({
                message: err?.message || 'Não foi possível carregar dados de operações.',
                code: err?.code || 'OPS_LOAD_FAILED',
            });
        } finally {
            setLoading(false);
            inFlight.current = false;
        }
    }, []);

    useEffect(() => {
        fetchSnapshot(initialRangeDays);
    }, [fetchSnapshot, initialRangeDays]);

    const ensureRange = useCallback(
        (neededDays) => {
            if (neededDays > loadedRangeDays) {
                fetchSnapshot(neededDays);
            }
        },
        [loadedRangeDays, fetchSnapshot],
    );

    const refetch = useCallback(() => {
        fetchSnapshot(Math.max(loadedRangeDays, DAYS_WINDOW_DEFAULT));
    }, [fetchSnapshot, loadedRangeDays]);

    return { snapshot, loading, error, ensureRange, refetch, loadedRangeDays };
}

export function OperacoesStoreProvider({ children, initialRangeDays = DAYS_WINDOW_DEFAULT }) {
    const loader = useLoader(initialRangeDays);
    const value = useMemo(() => loader, [loader]);
    return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useOperacoesStore() {
    const ctx = useContext(StoreContext);
    if (!ctx) {
        throw new Error('useOperacoesStore deve ser usado dentro de OperacoesStoreProvider');
    }
    return ctx;
}
